import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import moment from 'moment';
import { Calendar } from 'primeng';
import { ISystemSettings } from '../../interfaces/system-settings';
import { ITaskInfo, IVehicle } from '../../interfaces/task-info';
import { ITaskUpdate } from '../../interfaces/task-update';
import { MapService } from '../../services';
import { BBWTMessageService } from '../../services';
import { OrderService } from '../../services';
import { SettingsService } from '../../services';
import { TaskService } from '../../services';
import { VehicleService } from '../../services';

@Component({
  templateUrl: './order-details.component.html',
  styleUrls: ['order-details.component.css'],
})
export class OrderDetailsComponent implements OnInit {
  public orderTasks: ITaskInfo[];
  public selectedTask: ITaskInfo;
  public vehicle: IVehicle;
  public origVehicle: IVehicle;
  public isSearchVehicle: boolean = false;
  public showTaskDialog: boolean = false;
  public isLoaded: boolean = false;

  public dialogPickupEarliestMin: Date;
  public dialogPickupLatestMin: Date;
  public dialogDropoffEarliestMin: Date;
  public dialogDropoffLatestMin: Date;

  public currentRouteTime: any;
  public currentDistanceMiles: any;
  public systemSettings: ISystemSettings;
  public minutesInterval = 30; // default

  public id: number;

  public newPickupNote: string;
  public newDropoffNote: string;

  public regNumber: string;

  private _pickupEarliestDateTime: Date;
  public set pickupEarliestDateTime(value: Date) {
    if (value) {
      value.setSeconds(0);
      value.setMilliseconds(0);
    }
    this._pickupEarliestDateTime = value;

    // force validation on the dropoff times
    this.setPickupLatest();
    this.setDropoffEarliest();
    this.setDropoffLatest();
  }
  public get pickupEarliestDateTime(): Date {
    return this._pickupEarliestDateTime;
  }

  private _pickupLatestDateTime: Date;
  public set pickupLatestDateTime(value: Date) {
    if (value) {
      value.setSeconds(0);
      value.setMilliseconds(0);
    }
    this._pickupLatestDateTime = value;

    // force validation on times
    this.setDropoffEarliest();
    this.setDropoffLatest();
  }
  public get pickupLatestDateTime(): Date {
    return this._pickupLatestDateTime;
  }

  private _dropoffEarliestDateTime: Date;
  public set dropoffEarliestDateTime(value: Date) {
    this._dropoffEarliestDateTime = value;
    this.setDropoffEarliest();
  }
  public get dropoffEarliestDateTime(): Date {
    return this._dropoffEarliestDateTime;
  }

  private _dropoffLatestDateTime: Date;
  public set dropoffLatestDateTime(value: Date) {
    this._dropoffLatestDateTime = value;
    this.setDropoffLatest();
  }
  public get dropoffLatestDateTime(): Date {
    return this._dropoffLatestDateTime;
  }

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    public messageService: BBWTMessageService,
    private orderService: OrderService,
    private vehicleService: VehicleService,
    private taskService: TaskService,
    private mapService: MapService,
    private settingsService: SettingsService,
  ) {}

  ngOnInit() {
    this.activatedRoute.params.subscribe((params: Params) => {
      this.id = params['id'];

      if (this.id) {
        this.orderService.getTasksForOrderDetails(this.id).then((res) => {
          this.orderTasks = res;

          if (res.length) {
            this.selectedTask = res[0];
            this.setModelValues();
            this.setRouteTime();
          }

          this.isLoaded = true;
        });
      }
    });

    this.settingsService.GetSystemSettings().then((data: ISystemSettings) => {
      this.systemSettings = data;
      this.minutesInterval = data.createOrderTimeDayInterval;
    });
  }

  setModelValues() {
    let t = this.selectedTask;

    this._pickupEarliestDateTime = t.pickupEarliestDateTime;
    this._pickupLatestDateTime = t.pickupLatestDateTime;
    this._dropoffEarliestDateTime = t.dropoffEarliestDateTime;
    this._dropoffLatestDateTime = t.dropoffLatestDateTime;
    this.regNumber = t.vehicle.registrationNumber;

    this.newPickupNote = '';
    this.newDropoffNote = '';
  }

  close() {
    this.router.navigate(['app/customerinbox']);
  }

  updateCalendarUI(calendar: Calendar) {
    calendar.updateUI();
  }

  showEditTaskDialog(selectedTask: ITaskInfo) {
    this.selectedTask = selectedTask;
    this.setRouteTime();

    this.vehicle = selectedTask.vehicle;
    this.origVehicle = selectedTask.vehicle;

    this.dialogPickupEarliestMin = this.getDefaultPickupEarliestDateTime();
    this.pickupEarliestDateTime = this.dialogPickupEarliestMin;

    this.showTaskDialog = true;
  }

  setRouteTime() {
    //console.log("Setting route time..");

    let task = this.selectedTask;

    let pickup = task.pickupLocation;
    let dropoff = task.dropoffLocation;

    let pickupEarliest = new Date(task.pickupEarliestDateTime);

    const dto = this.mapService.GetRouteDTO(pickup, dropoff, pickupEarliest);
    this.mapService.GetRoute(dto).then((route) => {
      if (route != null) {
        this.currentRouteTime = route.duration;
        this.currentDistanceMiles = this.mapService.getMiles(route.distance);
      }
    });
  }

  setPickupLatest() {
    let latest = moment(this.pickupLatestDateTime).toDate();
    let defLatest = this.getDefaultPickupLatestDateTime(this._pickupEarliestDateTime);

    if (latest < defLatest) {
      let newLatest = this.roundUpTime(defLatest);
      this.pickupLatestDateTime = newLatest;
      this.dialogPickupLatestMin = newLatest;

      //console.log("Set pickup latest to: ", newLatest, "  - defLatest: ", defLatest);
    }
  }

  setDropoffEarliest() {
    let earliest = moment(this._dropoffEarliestDateTime).toDate();
    let defEarliest = moment(this.pickupEarliestDateTime).add(this.currentRouteTime, 's').toDate();

    if (earliest < defEarliest) {
      let newEarliest = this.roundUpTime(defEarliest);
      this.dropoffEarliestDateTime = newEarliest;
      this.dialogDropoffEarliestMin = newEarliest;
    }
  }

  setDropoffLatest() {
    let latest = moment(this._dropoffLatestDateTime).toDate();
    let defLatest = moment(this.pickupLatestDateTime).add(this.currentRouteTime, 's').toDate();

    //console.log("Setting dropoff latest: (pickup) ", this.pickupLatestDateTime, ", route time: ", this.currentRouteTime, "   - def: ", defLatest);

    if (latest < defLatest) {
      let newLatest = this.roundUpTime(defLatest);
      this.dropoffLatestDateTime = newLatest;
      this.dialogDropoffLatestMin = newLatest;

      //console.log("Set dropoff latest to: ", newLatest, "  - defLatest: ", defLatest);
    }
  }

  getDefaultPickupEarliestDateTime(): Date {
    return moment(Date()).add(24, 'h').toDate();
  }

  getDefaultPickupLatestDateTime(pickupEarliestDateTime: Date): Date {
    let createOrderTimeDayStart = this.systemSettings.createOrderTimeDayStart;
    let createOrderTimeDayEnd = this.systemSettings.createOrderTimeDayEnd;
    let createOrderPickUpLatestPlusDays = this.systemSettings.createOrderPickUpLatestPlusDays;
    let createOrderAfterEndPickUpLatestPlusDays =
      this.systemSettings.createOrderAfterEndPickUpLatestPlusDays;

    let pickupEarliestDateTimeMoment = moment(pickupEarliestDateTime);
    if (createOrderTimeDayStart) {
      let createOrderTimeDayStartMoment = moment(pickupEarliestDateTime)
        .startOf('day')
        .add(createOrderTimeDayStart, 'h');

      // 1st case
      if (pickupEarliestDateTimeMoment.isSameOrBefore(createOrderTimeDayStartMoment)) {
        if (createOrderPickUpLatestPlusDays) {
          return moment(pickupEarliestDateTime)
            .startOf('day')
            .add(createOrderPickUpLatestPlusDays, 'd')
            .add(createOrderTimeDayStart, 'h')
            .toDate();
        }
      }
    }

    if (createOrderTimeDayEnd) {
      let createOrderTimeDayEndMoment = moment(pickupEarliestDateTime)
        .startOf('day')
        .add(createOrderTimeDayEnd + 12, 'h');

      // 2nd case
      if (pickupEarliestDateTimeMoment.isSameOrBefore(createOrderTimeDayEndMoment)) {
        if (createOrderPickUpLatestPlusDays) {
          return moment(pickupEarliestDateTime).add(createOrderPickUpLatestPlusDays, 'd').toDate();
        }
      }
      // 3rd case
      else {
        if (createOrderAfterEndPickUpLatestPlusDays) {
          return moment(pickupEarliestDateTime)
            .add(createOrderAfterEndPickUpLatestPlusDays, 'd')
            .toDate();
        }
      }
    }

    // default
    return moment(pickupEarliestDateTime).add(12, 'h').toDate();
  }

  roundUpTime(time: Date) {
    if (time.getMinutes() % this.minutesInterval == 0 || time.getMinutes() == 0) {
      return time;
    }

    var min = this.minutesInterval;
    while (min < 60) {
      if (min >= time.getMinutes()) {
        break;
      }

      min += this.minutesInterval;
    }

    if (min > 60) {
      time.setMinutes(60);
    } else {
      time.setMinutes(min);
    }

    return time;
  }

  inputErrorToShow(c: any): string {
    return !c.valid && c.dirty && c.errors && c.errors.error ? c.errors.error : null;
  }

  dialogSaveTaskClick() {
    let taskUpdate: ITaskUpdate = {
      id: this.selectedTask.id,
      pickupEarliestDateTime: moment(this._pickupEarliestDateTime).format('YYYY-MM-DD HH:mm'),
      pickupLatestDateTime: moment(this._pickupLatestDateTime).format('YYYY-MM-DD HH:mm'),
      pickupNotes: this.newPickupNote,
      dropoffEarliestDateTime: moment(this._dropoffEarliestDateTime).format('YYYY-MM-DD HH:mm'),
      dropoffLatestDateTime: moment(this._dropoffLatestDateTime).format('YYYY-MM-DD HH:mm'),
      dropoffNotes: this.newDropoffNote,
      registrationNumber: this.regNumber,
    };

    this.taskService.checkIfCanChangeVehicle(taskUpdate).then((result) => {
      if (!result) {
        this.messageService.ShowError(
          'Save',
          'New vehicle is not in the same weight range as previous',
        );
        return;
      }

      this.orderService
        .updateTask(taskUpdate)
        .then(() => {
          this.messageService.ShowSuccess('Save', 'Task is saved');

          this.orderService.getTasksForOrderDetails(this.id).then((res) => {
            this.orderTasks = res;
            this.selectedTask = this.orderTasks.find((o) => o.id == taskUpdate.id);
          });
        })
        .catch(() => {
          this.messageService.ShowError('Save', 'Task saving error');
        });

      this.showTaskDialog = false;
    });

    //if (this.vehicle.kerbWeight > this.origVehicle.kerbWeight) {
    //    this.messageService.ShowWarning("Save", "Increase in vehicle weight is not allowed");
    //    return;
    //}
  }

  searchVehicle() {
    this.isSearchVehicle = true;
    this.vehicle = null;
    var that = this;
    this.vehicleService
      .GetByRegNum(this.regNumber)
      .then((vehicle) => {
        if (vehicle.registrationDate) vehicle.registrationDate = new Date(vehicle.registrationDate);

        that.vehicle = vehicle;
        that.isSearchVehicle = false;
        if (vehicle == null) that.messageService.ShowWarning('', 'Vehicle not Found');
      })
      .catch(() => {
        that.vehicle = null;
        that.isSearchVehicle = false;
        that.messageService.ShowWarning('', 'Vehicle not Found');
      });
  }

  getTravelTime(): string {
    return this.mapService.getTravelTimeFormatted(this.selectedTask.estimateTime);
  }
}
